import React, { useState } from 'react';
import { Card, Col } from 'react-bootstrap';
import { BLACK_300, MEDIUM_BLUE } from 'constants/colors';
import ReactECharts from 'components/ThemedECharts';
import HistoryRange from 'types/Meteorology/Weather/Area/historyRange';
import downloadFile from 'helper/downloadFile';
import { filterHistoryData } from 'helper/Meteorology/Weather/Area/History';
import range from 'helper/range';
import useParseHistoryCategories from 'hooks/Meteorology/Weather/Area/History/useParseHistoryCategories';
import Button from 'components/DesignSystem/Button';
import useIsMobile from 'utils/hooks/useIsMobile';
import FullscreenChart from 'components/Layout/MobileShell/FullscreenChart';
import { cssVar } from 'constants/theme';

import '../../Mobile/index.scss';
import DataRangeDisplay from '../../DataRangeDisplay';
import RangeSelector from '../../RangeSelector';

export default function MJOHistoryCard({
  amplitudeCategories,
  amplitudeValues,
  phaseCategories,
  phaseValues,
}: {
  amplitudeCategories: string[],
  amplitudeValues: number[],
  phaseCategories: string[],
  phaseValues: number[],
}) {
  const isMobile = useIsMobile();
  const [selectedRange, setSelectedRange] = useState<HistoryRange>('all');
  const [fullscreen, setFullscreen] = useState(false);
  const { filteredCategories, filteredValues: filteredAmplitudeValues } = filterHistoryData(
    new Date(),
    selectedRange,
    amplitudeValues,
    amplitudeCategories,
  );
  const { filteredValues: filteredPhaseValues } = filterHistoryData(
    new Date(),
    selectedRange,
    phaseValues,
    phaseCategories,
  );
  const { categories } = useParseHistoryCategories(filteredCategories);
  const rowsAvailable = Math.min(
    filteredCategories.length,
    filteredAmplitudeValues.length,
    filteredPhaseValues.length,
  );
  const downloadCsv = () => {
    const rows = range(rowsAvailable).map((i) => (
      `${filteredCategories[i]};${filteredAmplitudeValues[i].toLocaleString('pt-BR')};${filteredPhaseValues[i].toLocaleString('pt-BR')}`
    ));
    const csvData = btoa(`Data;Amplitude;Fase\n${rows.join('\n')}`);
    downloadFile(csvData, `historico_${selectedRange}.csv`);
  };

  const downloadButton = (
    <Button
      id="weather-history-download"
      icon="download"
      small
      className={isMobile ? '' : 'ms-3'}
      disabled={rowsAvailable < 1}
      onClick={downloadCsv}
    />
  );

  const chartOption = {
    grid: [
      {
        left: '4%',
        right: '4%',
        height: '25%',
      },
      {
        left: '4%',
        right: '4%',
        top: '60%',
        height: '25%',
      },
    ],
    xAxis: [
      {
        type: 'time',
        splitNumber: 10,
        boundaryGap: false,
        data: filteredCategories,
        show: false,
      },
      {
        gridIndex: 1,
        type: 'time',
        splitNumber: 10,
        boundaryGap: false,
        data: filteredCategories,
        ...(isMobile ? { axisLabel: { fontSize: 9, hideOverlap: true } } : {}),
      },
    ],
    yAxis: [
      {
        name: 'Amplitude',
        type: 'value',
      },
      {
        gridIndex: 1,
        name: 'Fase',
        type: 'value',
      },
    ],
    legend: {
      data: ['Amplitude', 'Fase'],
      ...(isMobile ? { show: false } : {}),
    },
    series: [
      {
        name: 'Amplitude',
        type: 'line',
        smooth: true,
        symbol: 'none',
        data: categories.map((date, index) => ({
          name: date,
          value: [date, filteredAmplitudeValues[index]],
        })),
      },
      {
        name: 'Fase',
        type: 'line',
        xAxisIndex: 1,
        yAxisIndex: 1,
        symbol: 'none',
        data: categories.map((date, index) => ({
          name: date,
          value: [date, filteredPhaseValues[index]],
        })),
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        animation: false,
      },
    },
    axisPointer: {
      link: [{ xAxisIndex: 'all' }],
    },
    animation: false,
    dataZoom: [
      {
        type: 'slider',
        show: 'true',
        xAxisIndex: [0, 1],
        top: '95%',
        start: 0,
        end: 100,
        filterMode: 'none',
        borderRadius: 32,
        height: 9,
        borderColor: BLACK_300, // tema: cor de dado
        fillerColor: MEDIUM_BLUE, // tema: cor de dado
        showDetail: false,
        showDataShadow: false,
        handleIcon: 'path://m50,50 a100,100 0 1,0 -200,0 a 100,100 0 1,0 200,0 Z',
        handleSize: 14,
        handleStyle: {
          borderColor: MEDIUM_BLUE, // tema: cor de dado
          borderWidth: 2,
        },
        moveHandleStyle: {
          opacity: 0,
        },
        brushSelect: false,
        brushStyle: {
          opacity: 0,
        },
        dataBackground: {
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            opacity: 0,
          },
        },
      },
      {
        type: 'slider',
        show: 'true',
        yAxisIndex: 0,
        right: '3%',
        start: 0,
        end: 100,
        filterMode: 'none',
        borderRadius: 32,
        width: 9,
        borderColor: BLACK_300, // tema: cor de dado
        fillerColor: MEDIUM_BLUE, // tema: cor de dado
        showDetail: false,
        showDataShadow: false,
        handleIcon: 'path://m50,50 a100,100 0 1,0 -200,0 a 100,100 0 1,0 200,0 Z',
        handleSize: 14,
        handleStyle: {
          borderColor: MEDIUM_BLUE, // tema: cor de dado
          borderWidth: 2,
        },
        moveHandleStyle: {
          opacity: 0,
        },
        brushSelect: false,
        brushStyle: {
          opacity: 0,
        },
        dataBackground: {
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            opacity: 0,
          },
        },
      },
      {
        type: 'slider',
        show: 'true',
        yAxisIndex: 1,
        right: '3%',
        start: 0,
        end: 100,
        filterMode: 'none',
        borderRadius: 32,
        width: 9,
        borderColor: BLACK_300, // tema: cor de dado
        fillerColor: MEDIUM_BLUE, // tema: cor de dado
        showDetail: false,
        showDataShadow: false,
        handleIcon: 'path://m50,50 a100,100 0 1,0 -200,0 a 100,100 0 1,0 200,0 Z',
        handleSize: 14,
        handleStyle: {
          borderColor: MEDIUM_BLUE, // tema: cor de dado
          borderWidth: 2,
        },
        moveHandleStyle: {
          opacity: 0,
        },
        brushSelect: false,
        brushStyle: {
          opacity: 0,
        },
        dataBackground: {
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            opacity: 0,
          },
        },
      },
    ],
  };

  const chart = (
    <div id="weather-history-chart" data-start={filteredCategories.at(0)} data-end={filteredCategories.at(-1)}>
      <ReactECharts
        opts={{ locale: 'pt_BR' }}
        option={chartOption}
        style={isMobile ? { height: '20rem' } : undefined}
      />
    </div>
  );

  if (isMobile) {
    const mobileTitle = 'Amplitude/Fase · Oscilação Madden-Julian (MJO)';
    return (
      <div className="history-card-mobile">
        <div className="hcm-head">
          <span className="hcm-title">{mobileTitle}</span>
          <div className="weather-history-actions">
            <button
              type="button"
              aria-label="Tela cheia"
              className="weather-history-fullscreen-trigger"
              onClick={() => setFullscreen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
            {downloadButton}
          </div>
        </div>
        <div className="hcm-range">
          <RangeSelector
            selection={selectedRange}
            setSelection={setSelectedRange}
          />
        </div>
        <DataRangeDisplay
          firstDate={amplitudeCategories.at(0) || ''}
          lastDate={amplitudeCategories.at(-1) || ''}
        />
        {chart}
        <FullscreenChart
          open={fullscreen}
          dismiss={() => setFullscreen(false)}
          title={mobileTitle}
        >
          <ReactECharts
            opts={{ locale: 'pt_BR' }}
            option={chartOption}
            className="h-100"
            style={{ height: '100%' }}
          />
        </FullscreenChart>
      </div>
    );
  }

  return (
    <Card className="shadow-sm forecast-instruction-card m-5">
      <Col className="flex-fill py-5 h-100">
        <div className="d-flex px-5 justify-content-between align-items-center">
          <div className="text-primary">Amplitude/Fase - Oscilação Madden-Julian (MJO)</div>
          <div className="text-primary fw-bold d-flex align-items-center">
            Baixar dados em CSV
            {downloadButton}
          </div>
        </div>
        <div className="mt-5">
          <div className="d-flex justify-content-between align-items-center px-5">
            <RangeSelector
              selection={selectedRange}
              setSelection={setSelectedRange}
            />
            <DataRangeDisplay
              firstDate={amplitudeCategories.at(0) || ''}
              lastDate={amplitudeCategories.at(-1) || ''}
            />
          </div>
          {chart}
        </div>
      </Col>
    </Card>
  );
}
